import { useEffect, useState } from 'react';
import client from '../Contentful_API/api';
import fetchAllEntries from './fetchAllEntries';

/**
 * Fetches Contentful entries and tracks loading/error state.
 *
 * The query object is compared by value (serialised) rather than by identity so
 * callers can pass a literal without triggering a fetch on every render.
 */
export default function useEntries(query, { all = false } = {}) {
    const key = JSON.stringify(query);
    const [state, setState] = useState({ items: [], loading: true, error: false });

    useEffect(() => {
        let active = true;
        setState((previous) => ({ ...previous, loading: true, error: false }));

        const request = all
            ? fetchAllEntries(client, JSON.parse(key), () => active)
            : client.getEntries(JSON.parse(key));
        request
            .then((response) => {
                if (!active) return;
                setState({ items: response.items || [], loading: false, error: false });
            })
            .catch(() => {
                if (!active) return;
                setState({ items: [], loading: false, error: true });
            });

        return () => {
            active = false;
        };
    }, [key, all]);

    return state;
}

/** Strips CMS markup so a body field can be reused as a short card summary. */
export function toExcerpt(value, length = 160) {
    if (!value || typeof value !== 'string') return '';
    const text = value
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
    if (text.length <= length) return text;
    return `${text.slice(0, text.lastIndexOf(' ', length) || length)}…`;
}

/** Formats a CMS date as e.g. "12 March 2026"; returns '' when unparseable. */
export function formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

/** Resolves the first usable asset URL from a Contentful media field. */
export function assetUrl(field) {
    if (!field) return '';
    const asset = Array.isArray(field) ? field[0] : field;
    const url = asset?.fields?.file?.url || '';
    if (!url) return '';
    return url.startsWith('//') ? `https:${url}` : url;
}
